import { useEffect } from 'react';

/**
 * Scroll-reveal: observes every element carrying the `.ds-reveal` class
 * inside `rootSelector` and toggles `.in-view` as they enter the viewport,
 * driving the CSS fade + slide transition. Re-runs whenever `deps` change
 * so elements rendered after async data loads are still picked up.
 */
export const useScrollReveal = (
  rootSelector = '.dashboard-page',
  deps: unknown[] = [],
): void => {
  useEffect(() => {
    if (typeof window === 'undefined' || !('IntersectionObserver' in window)) {
      // Fallback: reveal everything immediately.
      document
        .querySelectorAll(`${rootSelector} .ds-reveal`)
        .forEach((el) => el.classList.add('in-view'));
      return;
    }

    const root = document.querySelector(rootSelector);
    if (!root) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('in-view');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12, rootMargin: '0px 0px -8% 0px' },
    );

    const els = root.querySelectorAll('.ds-reveal:not(.in-view)');
    els.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rootSelector, ...deps]);
};

export default useScrollReveal;
